<template>
  <div :class="`flex ${align} no-wrap`">
    <div class="col-7">
      <div class="q-mb-sm text-bold rating-header">{{ props.title }}</div>
      <div v-if="props.description" class="q-mb-sm rating-description">
        {{ props.description }}
      </div>
    </div>
    <div>
      <div class="q-pl-sm row justify-between col-5">
        <div
          v-for="icon of ratings"
          :key="icon.rank"
          :class="`row justify-center items-center rating-button-wrap col max-width rating-icon ${
            props[icon.rank] ? 'active' : 'inactive'
          } ${editable ? 'cursor-pointer' : ''}`"
          :data-testid="`${dataTestid ?? title.toLowerCase().replaceAll(' ', '-')}-${icon.rank}`"
        >
          <button v-if="editable" type="button" class="rating-button" @click="clicked(icon)">
            <img :src="`/images/${icon.fileName}.png`" class="full-width q-pa-sm" :alt="icon.fileName" />
          </button>
          <img v-else :src="`/images/${icon.fileName}.png`" class="full-width q-pa-sm" :alt="icon.fileName" />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref } from 'vue';

/*****************************************************************************
 * PROPS
 ****************************************************************************/
const props = withDefaults(
  defineProps<{
    bad?: boolean;
    ok?: boolean;
    great?: boolean;
    title?: string;
    description?: string;
    editable?: boolean;
    required?: boolean;
    align?: 'row' | 'column';
    dataTestid?: string;
  }>(),
  {
    bad: false,
    ok: false,
    great: false,
    title: 'Title',
    description: undefined,
    editable: false,
    required: false,
    align: 'column',
    dataTestid: undefined,
  },
);

/*****************************************************************************
 * EMITS
 ****************************************************************************/
const emit = defineEmits(['update:bad', 'update:ok', 'update:great']);
/*****************************************************************************
 * SETUP
 ****************************************************************************/
const clicked = (rating: Rating) => {
  if (props.editable) {
    const oldValue = props[rating.rank];
    if (oldValue) {
      if (!props.required) {
        emit(`update:${rating.rank}`, false);
      }
    } else {
      emit(`update:${rating.rank}`, true);
      for (const otherIcon of ratings.value) {
        // eslint-disable-next-line sonarjs/nested-control-flow
        if (otherIcon.rank !== rating.rank) {
          emit(`update:${otherIcon.rank}`, false);
        }
      }
    }
  }
};

const ratings = ref<Rating[]>([
  {
    rank: 'bad',
    fileName: 'rating-bad',
    cssClass: 'rating-bad',
  },
  {
    rank: 'ok',
    fileName: 'rating-ok',
    cssClass: 'rating-ok',
  },
  {
    rank: 'great',
    fileName: 'rating-good',
    cssClass: 'rating-good',
  },
]);

interface Rating {
  rank: 'bad' | 'ok' | 'great';
  fileName: string;
  cssClass: string;
}
</script>
<style scoped lang="scss">
@import 'styles/app.variables.scss';
@import 'styles/quasar.variables.scss';
.inactive {
  opacity: 0.2;
}

.rating-button {
  display: block;
  width: 100%;
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  color: inherit;
  cursor: pointer;

  &:hover {
    background-color: transparent;
  }
}

.rating-icon {
  max-width: 110px;
}

.rating-description {
  min-height: 100px;
}

@media screen and (max-width: $bp600) {
  .rating-description {
    font-size: 0.95em;
  }
}

@media screen and (max-width: 660px) {
  .column .rating-description {
    min-height: 130px;
  }
}
@media screen and (max-width: 600px) {
  .column .rating-description {
    min-height: 150px;
  }
}
@media screen and (max-width: 500px) {
  .column .rating-description {
    min-height: 180px;
  }
}
@media screen and (max-width: 450px) {
  .column .rating-description {
    min-height: 220px;
  }
}
@media screen and (max-width: 400px) {
  .column .rating-description {
    min-height: 250px;
  }
}
@media screen and (max-width: 370px) {
  .column .rating-header {
    min-height: 50px;
  }
}
</style>
